<template>
  <el-dialog 
    title="手动快录" 
    :visible="visible" 
    :before-close="handleBeforeCloseAsCancel"
    class="manual-quick-record-dialog"
    @opened="handleOpened"
    :close-on-click-modal="false"
    :close-on-press-escape="false">
    <el-transfer
      v-model="internalSelected"
      :data="data"
      :titles="['待选择', '已选择']"
      :button-texts="['移除', '添加']"
      filterable
      :filter-method="filterMethod"
      filter-placeholder="请输入学号、姓名或拼音首字母"
      class="manual-quick-record-transfer"
      ref="manualQuickRecordTransfer"
      target-order="push">
    </el-transfer>
    <!-- 自定义按钮 -->
    <div style="display: flex; justify-content: center; margin-top: 10px;">
      <el-button 
        size="mini" 
        @click="moveToRight" 
        @touchstart.native.prevent="moveToRight" 
        style="margin: 0 10px;" 
        title="到右边" 
        class="manual-quick-record-btn" 
        plain>
        <i class="el-icon-arrow-right"></i>
      </el-button>
      <el-button 
        size="mini" 
        @click="moveToLeft" 
        @touchstart.native.prevent="moveToLeft" 
        style="margin: 0 10px;" 
        title="到左边" 
        class="manual-quick-record-btn" 
        plain>
        <i class="el-icon-arrow-left"></i>
      </el-button>
    </div>
    <div slot="footer" class="dialog-footer">
      <div class="footer-left">
        <el-button 
          v-if="!isAssigned"
          type="success" 
          @click="handleSaveToCloud" 
          @touchstart.native.prevent="handleSaveToCloud"
          :loading="savingToCloud"
          class="save-to-cloud-btn">
          暂存至云端
        </el-button>
        <el-tooltip 
          v-if="!isAssigned"
          class="item tooltip-custom" 
          effect="light" 
          popper-class="manual-quick-record-tooltip"
          placement="top">
          <template slot="content">
            <div class="tooltip-content">
              该按钮用于暂存当前已选中学生数据到云端，暂存7天，在您点击"布置作业按钮"的时候会自动使用本订正数据，使用后本数据将不再缓存。<br><br>
              本功能适用于老师改完了作业想快速录入学生作业数据但是暂时不想布置作业的场景（比如老师想当作业本发到全体学生手上后再点击布置作业）。
            </div>
          </template>
          <i class="el-icon-question" style="margin-left: 8px; font-size: 16px; color: #909399;"></i>
        </el-tooltip>
      </div>
      <div class="footer-right">
        <el-button @click="handleCancel" @touchstart.native.prevent="handleCancel">取 消</el-button>
        <el-button type="primary" @click="handleConfirm" @touchstart.native.prevent="handleConfirm">{{ confirmButtonText }}</el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: 'MobileManualQuickRecordDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    selected: {
      type: Array,
      default: () => []
    },
    data: {
      type: Array,
      default: () => []
    },
    isAssigned: {
      type: Boolean,
      default: true
    },
    subjectClass: {
      type: String,
      default: 'math-subject'
    }
  },
  data() {
    return {
      inputHandler: null, // 用于存储输入事件处理器，以便后续移除
      internalSelected: [], // 内部维护的选中状态
      savingToCloud: false // 暂存到云端的加载状态
    }
  },
  computed: {
    confirmButtonText() {
      return this.isAssigned ? '确 定' : '确定并布置'
    }
  },
  watch: {
    // 监听外部传入的 selected prop，同步到内部状态
    selected: {
      handler(newVal) {
        // 更新内部状态，这将触发 el-transfer 的更新
        this.internalSelected = [...newVal];
      },
      immediate: true,
      deep: true
    },
    // 监听内部状态变化，同步到外部
    internalSelected: {
      handler(newVal, oldVal) {
        // 当内部状态变化时，通知外部更新
        if (JSON.stringify(newVal) !== JSON.stringify(this.selected)) {
          this.$emit('update:selected', [...newVal]);
        }
      },
      deep: true
    }
  },
  methods: {
    // 暂存到云端
    async handleSaveToCloud() {
      if (this.internalSelected.length === 0) {
        this.$message.warning('请先选择要暂存的学生');
        return;
      }
      
      // 检查左侧是否有勾选的学生
      if (this.hasLeftCheckedStudents()) {
        this.$confirm('您左侧仍有勾选着的学生未处理，确定暂存右侧列表至云端？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '返回',
          type: 'warning',
          customClass: this.subjectClass + '-confirm'
        }).then(() => {
          // 用户确认暂存，执行暂存操作
          this.executeSaveToCloud();
        }).catch(() => {
          // 用户点击返回，不执行任何操作
        });
      } else {
        // 左侧没有勾选的学生，直接暂存
        this.executeSaveToCloud();
      }
    },
    
    // 执行暂存操作
    async executeSaveToCloud() {
      this.savingToCloud = true;
      
      try {
        // 获取当前日期和作业类型
        const { currentDate } = this.$parent;
        const classId = this.$parent.classId;
        const typeId = this.$parent.localSelectedType;
        
        // 调用API保存暂存数据
        const response = await this.$http.post('/manualQuickRecordTemp', {
          studentIds: this.internalSelected
        }, {
          params: {
            classId: classId,
            date: currentDate,
            typeId: typeId
          }
        });
        
        if (response.data.success) {
          this.$message.success('暂存成功，数据将在7天后过期');
          // 暂存成功后清空数据并关闭弹窗
          this.clearTransferSelection();
          this.internalSelected = [];
          this.$emit('update:visible', false);
        } else {
          this.$message.error(response.data.message || '暂存失败');
        }
      } catch (error) {
        console.error('暂存失败:', error);
        this.$message.error('暂存失败，请稍后重试');
      } finally {
        this.savingToCloud = false;
      }
    },
    
    // 检查左侧是否还有勾选的学生
    hasLeftCheckedStudents() {
      const transfer = this.$refs.manualQuickRecordTransfer;
      if (transfer && transfer.leftChecked && transfer.leftChecked.length > 0) {
        return true;
      }
      return false;
    },
    
    // 清空穿梭框组件内部的选中状态
    clearTransferSelection() {
      const transfer = this.$refs.manualQuickRecordTransfer;
      if (transfer) {
        // 清空左侧面板的选中状态
        if (transfer.leftChecked) {
          transfer.leftChecked.splice(0);
        }
        
        // 清空右侧面板的选中状态
        if (transfer.rightChecked) {
          transfer.rightChecked.splice(0);
        }
        
        // 清空搜索关键词
        if (transfer.$children && transfer.$children[0]) {
          transfer.$children[0].query = '';
        }
      }
    },
    
    handleClose() {
      // 注意：由于before-close会处理关闭逻辑，这里只需作为后备处理
      // 当使用handleBeforeCloseAsCancel时，handleClose不应该被触发
      // 但如果因为某些原因被触发，我们仍然需要清空状态
      this.clearTransferSelection(); // 清空穿梭框内部的选中状态
      this.$emit('update:visible', false);
    },
    
    handleBeforeClose(done) {
      // 检查左侧是否有勾选的学生
      if (this.hasLeftCheckedStudents()) {
        // 弹出确认对话框
        this.$confirm('您左侧仍有勾选着的学生未处理，确定退出？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '返回',
          type: 'warning',
          customClass: this.subjectClass + '-confirm'
        }).then(() => {
          // 用户确认退出，执行关闭操作
          this.clearTransferSelection(); // 清空穿梭框内部的选中状态
          this.$emit('update:visible', false);
          done();
        }).catch(() => {
          // 用户点击返回，不执行任何操作，继续保持弹窗打开
        });
      } else {
        // 左侧没有勾选的学生，直接关闭
        this.clearTransferSelection(); // 清空穿梭框内部的选中状态
        this.$emit('update:visible', false);
        done();
      }
    },
    
    // 作为取消按钮的关闭前处理方法
    handleBeforeCloseAsCancel(done) {
      // 检查左侧是否有勾选的学生
      if (this.hasLeftCheckedStudents()) {
        // 弹出确认对话框
        this.$confirm('您左侧仍有勾选着的学生未处理，确定退出？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '返回',
          type: 'warning',
          customClass: this.subjectClass + '-confirm'
        }).then(() => {
          // 用户确认退出，执行取消操作
          this.clearTransferSelection(); // 清空穿梭框内部的选中状态
          this.internalSelected = []; // 清空内部选中状态，确保下次打开时右侧为空
          this.$emit('cancel'); // 发出cancel事件，与取消按钮行为一致
          done(); // 关闭弹窗
        }).catch(() => {
          // 用户点击返回，不执行任何操作，继续保持弹窗打开
          done(false); // 取消关闭操作
        });
      } else {
        // 左侧没有勾选的学生，直接取消
        this.clearTransferSelection(); // 清空穿梭框内部的选中状态
        this.internalSelected = []; // 清空内部选中状态，确保下次打开时右侧为空
        this.$emit('cancel'); // 发出cancel事件，与取消按钮行为一致
        done(); // 关闭弹窗
      }
    },
    
    handleCancel() {
      // 检查左侧是否有勾选的学生
      if (this.hasLeftCheckedStudents()) {
        // 弹出确认对话框
        this.$confirm('您左侧仍有勾选着的学生未处理，确定退出？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '返回',
          type: 'warning',
          customClass: this.subjectClass + '-confirm'
        }).then(() => {
          // 用户确认退出，执行关闭操作
          this.clearTransferSelection(); // 清空穿梭框内部的选中状态
          this.internalSelected = []; // 清空内部选中状态，确保下次打开时右侧为空
          this.$emit('cancel');
        }).catch(() => {
          // 用户点击返回，不执行任何操作，继续保持弹窗打开
        });
      } else {
        // 左侧没有勾选的学生，直接关闭
        this.clearTransferSelection(); // 清空穿梭框内部的选中状态
        this.internalSelected = []; // 清空内部选中状态，确保下次打开时右侧为空
        this.$emit('cancel');
      }
    },
    
    handleConfirm() {
      // 检查左侧是否有勾选的学生
      if (this.hasLeftCheckedStudents()) {
        // 弹出确认对话框
        this.$confirm('您左侧仍有勾选着的学生未处理，确定退出？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '返回',
          type: 'warning'
        }).then(() => {
          // 用户确认退出，执行确认操作
          this.clearTransferSelection(); // 清空穿梭框内部的选中状态
          this.$emit('confirm', [...this.internalSelected]); // 传递当前选中的学生ID列表
        }).catch(() => {
          // 用户点击返回，不执行任何操作，继续保持弹窗打开
        });
      } else {
        // 左侧没有勾选的学生，直接确认
        this.clearTransferSelection(); // 清空穿梭框内部的选中状态
        // 确保数据同步后再发出确认事件
        this.$nextTick(() => {
          // 再次延迟确保父组件有足够时间更新
          setTimeout(() => {
            this.$emit('confirm', [...this.internalSelected]); // 传递当前选中的学生ID列表
          }, 50);
        });
      }
    },
    
    moveToRight() {
      // 获取穿梭框组件
      const transfer = this.$refs.manualQuickRecordTransfer;
      if (transfer && transfer.leftChecked && transfer.leftChecked.length > 0) {
        // 获取当前选中的学生项，保持原有顺序
        const selectedItems = this.data.filter(item => 
          transfer.leftChecked.includes(item.key)
        );
        
        // 创建一个新的数组来存储选中的学生key
        const selectedKeys = selectedItems.map(item => item.key);
        
        // 从现有的已选择列表中移除这些选中的学生（以防重复）
        const remainingKeys = this.internalSelected.filter(key => 
          !selectedKeys.includes(key)
        );
        
        // 将选中的学生放在顶部（后来居上），其余学生保持原有顺序
        const newSelected = [...selectedKeys, ...remainingKeys];
        
        // 更新内部选中状态，这将触发 internalSelected 的 watch
        this.internalSelected = newSelected;
        
        // 清空左侧面板的选中状态
        transfer.leftChecked.splice(0);
      }
    },
    
    moveToLeft() {
      // 获取穿梭框组件
      const transfer = this.$refs.manualQuickRecordTransfer;
      if (transfer && transfer.rightChecked && transfer.rightChecked.length > 0) {
        // 从已选择列表中移除右侧面板中选中的项，保持"后来居上"逻辑
        const newSelected = [...this.internalSelected];
        transfer.rightChecked.forEach(key => {
          const index = newSelected.indexOf(key);
          if (index > -1) {
            newSelected.splice(index, 1);
          }
        });
        
        // 更新内部选中状态
        this.internalSelected = newSelected;
        
        // 清空右侧面板的选中状态
        transfer.rightChecked.splice(0);
      }
    },
    
    filterMethod(query, item) {
      // 如果查询为空，显示所有项
      if (!query) {
        return true;
      }
      
      // 特殊处理：如果查询是单个"0"，显示所有带前导零的学号（01-09）以及个位数带零的学号（10、20、30等）
      if (query === '0') {
        const studentNum = parseInt(item.studentNumber);
        // 显示1-9号学生（显示为01-09）以及个位数为0的学生（10、20、30等）
        return (studentNum >= 1 && studentNum <= 9) || (studentNum % 10 === 0);
      }
      // 特殊处理：如果查询以"0"开头（如"01", "02"等），精确匹配对应的学号
      else if (query.startsWith('0') && query.length > 1) {
        // 去掉前导零进行比较
        const normalizedQuery = query.replace(/^0+(\d+)$/, '$1');
        if (item.studentNumber === normalizedQuery) {
          return true;
        }
      }
      // 普通学号匹配
      else if (item.studentNumber.includes(query)) {
        return true;
      }
      // 姓名匹配
      else if (item.name.includes(query)) {
        return true;
      }
      // 拼音首字母匹配
      else if (item.pinyinInitials && item.pinyinInitials.toLowerCase().includes(query.toLowerCase())) {
        return true;
      }
      
      return false;
    },
    
    handleOpened() {
      // 弹窗打开后聚焦到左侧面板的搜索框
      this.$nextTick(() => {
        setTimeout(() => {
          // 获取穿梭框组件
          const transfer = this.$refs.manualQuickRecordTransfer;
          if (transfer) {
            // 聚焦到左侧面板的搜索框
            const inputs = transfer.$el.querySelectorAll('.el-transfer-panel:first-child .el-input__inner');
            if (inputs && inputs.length > 0) {
              // 设置输入框属性，使移动设备默认显示数字键盘
              inputs[0].setAttribute('inputmode', 'numeric');
              inputs[0].setAttribute('pattern', '[0-9]*');
              inputs[0].focus();
              
              // 保存事件处理器的引用，以便后续移除
              this.inputHandler = (event) => {
                // 获取输入值
                const value = event.target.value;
                
                // 检查是否是纯数字输入
                if (value && /^\d+$/.test(value)) {
                  // 查找完全匹配的学生（输入值与显示的学号完全一致）
                  const exactMatches = this.data.filter(item => {
                    // 获取学号前缀（对于1到9号学生是01-09，其他是原始学号）
                    let displayNumber = item.studentNumber;
                    const studentNum = parseInt(item.studentNumber);
                    if (studentNum >= 1 && studentNum <= 9) {
                      displayNumber = '0' + studentNum;
                    }
                    
                    // 检查输入值是否与显示的学号完全一致
                    return displayNumber === value;
                  });
                  
                  // 只有在完全匹配且唯一的情况下才自动添加
                  if (exactMatches.length === 1 && 
                      !this.internalSelected.includes(exactMatches[0].key)) {
                    // 自动添加到已选中列表（插入到开头以实现后添加的在上）
                    const studentKey = exactMatches[0].key;
                    const newSelected = [studentKey, ...this.internalSelected];
                    
                    // 更新内部选中状态
                    this.internalSelected = newSelected;
                    
                    // 清空搜索框
                    this.$nextTick(() => {
                      event.target.value = '';
                      // 手动触发input事件以更新组件状态
                      const inputEvent = new Event('input', { bubbles: true });
                      event.target.dispatchEvent(inputEvent);
                    });
                  }
                }
              };
              
              // 为输入框添加事件监听器
              if (inputs && inputs.length > 0) {
                const input = inputs[0];
                // 移除旧的事件监听器（如果有的话）
                input.removeEventListener('input', this.inputHandler);
                // 添加新的事件监听器
                input.addEventListener('input', this.inputHandler);
              }
            }
          }
        }, 100);
      });
    }
  }
};
</script>

<style scoped>
/* 手动快录弹窗样式 */
.manual-quick-record-dialog ::v-deep .el-dialog {
  margin: 54px 10px 10px 10px !important;
  width: calc(100% - 20px) !important;
}

/* 弹窗主体内边距 */
.manual-quick-record-dialog ::v-deep .el-dialog__body {
  padding: 15px !important; /* 按照原始设计设置为15px */
}

/* 弹窗头部样式 */
.manual-quick-record-dialog ::v-deep .el-dialog__header {
  padding: 15px 20px !important;
  position: relative;
}

.manual-quick-record-dialog ::v-deep .el-dialog__headerbtn {
  top: 50%;
  transform: translateY(-50%);
  right: 15px;
}

/* 针对手动快录穿梭框的移动端样式 */
.manual-quick-record-transfer ::v-deep .el-transfer {
  display: flex !important;
  flex-direction: row !important;
  align-items: stretch !important;  /* 使面板垂直拉伸对齐 */
  justify-content: space-between !important;
}

.manual-quick-record-transfer ::v-deep .el-transfer-panel {
  flex: 1 !important;
  width: 50% !important;  /* 设置为50%，最大化利用屏幕宽度 */
  min-height: 300px !important;
  margin: 0 !important;  /* 移除左右边距，最大化利用屏幕空间 */
}

/* 隐藏原生穿梭按钮 */
.manual-quick-record-transfer ::v-deep .el-transfer__buttons {
  display: none !important;
}

/* 自定义移动按钮样式 - 设置特定的padding */
.manual-quick-record-btn {
  padding: 7px 15px !important;
  border-radius: 8px !important; /* 方形带圆角 */
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

/* 对话框footer样式 */
.dialog-footer {
  display: flex !important;
  justify-content: space-between !important;
  align-items: center !important;
  width: 100%;
}

/* 对话框footer的左右padding */
.manual-quick-record-dialog ::v-deep .el-dialog__footer {
  padding-left: 10px !important;
  padding-right: 10px !important;
  display: flex !important;
  justify-content: space-between !important;
  align-items: center !important;
}

/* footer左右容器 */
.footer-left, .footer-right {
  display: flex;
  align-items: center;
}

/* 对话框底部按钮样式 */
.dialog-footer ::v-deep .el-button {
  border-radius: 12px !important; /* 底部按钮更大的圆角 */
}

/* 暂存至云端按钮样式 */
.save-to-cloud-btn {
  font-size: 14px !important;
  padding: 10px 15px !important;
}

.save-to-cloud-btn i {
  margin-right: 4px;
}

/* 选择器样式优化 */
.el-select-dropdown {
  border-radius: 10px;
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
  user-select: text;
}

/* 二次确认弹窗按钮样式 - 已在全局CSS中处理 */
</style>

<style>
/* 全局样式 - 不使用scoped */
.manual-quick-record-tooltip .tooltip-content {
  font-size: 14px !important;
  line-height: 1.8 !important;
}

.manual-quick-record-tooltip {
  max-width: 300px !important;
}

/* 主题色确认对话框样式 */
.chinese-subject-confirm.el-message-box .el-message-box__header {
  background: linear-gradient(135deg, #ff9800 0%, #ff6d00 100%) !important;
}

.chinese-subject-confirm.el-message-box .el-message-box__title {
  color: #fff !important;
}

.chinese-subject-confirm.el-message-box .el-message-box__btns .el-button--primary {
  background: linear-gradient(135deg, #ff9800 0%, #ff6d00 100%) !important;
  border-color: #ff6d00 !important;
}

.math-subject-confirm.el-message-box .el-message-box__header {
  background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%) !important;
}

.math-subject-confirm.el-message-box .el-message-box__title {
  color: #fff !important;
}

.math-subject-confirm.el-message-box .el-message-box__btns .el-button--primary {
  background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%) !important;
  border-color: #1976d2 !important;
}

.english-subject-confirm.el-message-box .el-message-box__header {
  background: linear-gradient(135deg, #e91e63 0%, #c2185b 100%) !important;
}

.english-subject-confirm.el-message-box .el-message-box__title {
  color: #fff !important;
}

.english-subject-confirm.el-message-box .el-message-box__btns .el-button--primary {
  background: linear-gradient(135deg, #e91e63 0%, #c2185b 100%) !important;
  border-color: #c2185b !important;
}
</style>